import React, { createContext, useContext, type ReactNode, useMemo } from "react";
import { type DecisionGraph } from "../../../utils";

/** The graph the graph blocks read: a DecisionGraph (convert other forms with DecisionGraph.from where they come in). */
export type ProvidedGraph = DecisionGraph;

export interface GraphContextValue {
  graph: ProvidedGraph | null;
}

const GraphContext = createContext<GraphContextValue | null>(null);

export const useGraph = (): GraphContextValue | null => {
  const context = useContext(GraphContext);
  return context;
};

export interface GraphProviderProps {
  children: ReactNode;
  graph: ProvidedGraph;
}

const GraphProvider = (props: GraphProviderProps) => {
  const { children, graph } = props;

  const resolvedContext = useMemo(() => {
    return { graph } satisfies GraphContextValue;
  }, [graph]);

  return <GraphContext.Provider value={resolvedContext}>{children}</GraphContext.Provider>;
};

export default GraphProvider;

export interface GraphDependantProps {
  graph?: ProvidedGraph;
}

export const useGraphFromPropsOrContext = (
  props: GraphDependantProps,
): {
  graph: ProvidedGraph | undefined;
  withGraphProvider: (children: ReactNode) => ReactNode;
} => {
  const { graph } = props;
  const context = useGraph();
  const resolvedGraph = graph || context?.graph;
  return {
    graph: resolvedGraph || undefined,
    withGraphProvider: (children: ReactNode) => {
      if (graph) {
        return <GraphProvider graph={graph}>{children}</GraphProvider>;
      }
      return children;
    },
  };
};
